import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import CursoService from '../../services/curso.service';

const DeletedCursosList = () => {
  const { hasPermission } = useAuth();
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [restoreDialog, setRestoreDialog] = useState({ open: false, id: null, name: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchDeletedCursos();
  }, []);

  const fetchDeletedCursos = async () => {
    setLoading(true);
    try {
      const response = await CursoService.getTrashed();
      setCursos(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching deleted cursos:', error);
      showSnackbar('Error loading deleted cursos: ' + (error.response?.data?.message || error.message), 'error');
      setCursos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (id, name) => {
    setRestoreDialog({ open: true, id, name });
  };

  const confirmRestore = async () => {
    try {
      await CursoService.restore(restoreDialog.id);
      showSnackbar('Curso restored successfully');
      fetchDeletedCursos();
    } catch (error) {
      showSnackbar('Error restoring curso', 'error');
    } finally {
      setRestoreDialog({ open: false, id: null, name: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredCursos = cursos.filter(curso =>
    curso.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton onClick={() => navigate('/cursos')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">Deleted Cursos</Typography>
            </Box>
          </Box>

          <TextField
            placeholder="Search deleted cursos by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Deleted At</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredCursos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No deleted cursos found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCursos.map((curso) => (
                    <TableRow key={curso.id} hover>
                      <TableCell>{curso.name}</TableCell>
                      <TableCell>{formatDate(curso.deleted_at)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPermission('cursos.edit') && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleRestore(curso.id, curso.name)}
                              title="Restore Curso"
                            >
                              <RestoreIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={restoreDialog.open}
        onClose={() => setRestoreDialog({ open: false, id: null, name: null })}
      >
        <DialogTitle>Restore Curso</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to restore <strong>{restoreDialog.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog({ open: false, id: null, name: null })}>
            Cancel
          </Button>
          <Button onClick={confirmRestore} color="primary" variant="contained">
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeletedCursosList;
